import { useState, useCallback, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

// Tool modes
export const TOOL_MODES = {
  SELECT: 'select',
  PAN: 'pan',
  DRAW: 'draw',
  CONNECTOR: 'connector',
};

// Default colors for nodes
export const NODE_COLORS = [
  { id: 'slate', bg: '#1e293b', border: '#334155', text: '#f1f5f9' },
  { id: 'purple', bg: '#3b1c5a', border: '#6b21a8', text: '#e9d5ff' },
  { id: 'blue', bg: '#1e3a5f', border: '#1d4ed8', text: '#bfdbfe' },
  { id: 'green', bg: '#14532d', border: '#15803d', text: '#bbf7d0' },
  { id: 'amber', bg: '#451a03', border: '#b45309', text: '#fde68a' },
  { id: 'red', bg: '#450a0a', border: '#b91c1c', text: '#fecaca' },
  { id: 'pink', bg: '#500724', border: '#be185d', text: '#fbcfe8' },
  { id: 'cyan', bg: '#083344', border: '#0891b2', text: '#a5f3fc' },
];

// Create empty initial data
const createEmptyData = () => ({
  nodes: {},
  rootId: null,
});

// Create default sample data for new maps
const createDefaultData = () => {
  const rootId = uuidv4();
  const child1Id = uuidv4();
  const child2Id = uuidv4();
  const child3Id = uuidv4();

  return {
    nodes: {
      [rootId]: {
        id: rootId,
        title: 'Central Idea',
        description: 'Start your mind map here',
        color: NODE_COLORS[0],
        x: 400,
        y: 300,
        parentId: null,
        collapsed: false,
      },
      [child1Id]: {
        id: child1Id,
        title: 'Branch 1',
        description: 'First main topic',
        color: NODE_COLORS[1],
        x: 150,
        y: 150,
        parentId: rootId,
        collapsed: false,
      },
      [child2Id]: {
        id: child2Id,
        title: 'Branch 2',
        description: 'Second main topic',
        color: NODE_COLORS[2],
        x: 650,
        y: 150,
        parentId: rootId,
        collapsed: false,
      },
      [child3Id]: {
        id: child3Id,
        title: 'Branch 3',
        description: 'Third main topic',
        color: NODE_COLORS[3],
        x: 400,
        y: 500,
        parentId: rootId,
        collapsed: false,
      },
    },
    rootId,
  };
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const useMindMap = (mapId = 'default') => {
  const [data, setData] = useState(createEmptyData);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const [isDraggingNode, setIsDraggingNode] = useState(false);
  const [toolMode, setToolMode] = useState(TOOL_MODES.SELECT);
  const [drawings, setDrawings] = useState([]);
  const [customConnectors, setCustomConnectors] = useState([]);
  const [connectorStart, setConnectorStart] = useState(null);
  const [currentDrawing, setCurrentDrawing] = useState(null);
  const [drawColor, setDrawColor] = useState('#ffffff');
  
  // Save state
  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle', 'saving', 'saved', 'error'
  const [isLoading, setIsLoading] = useState(true);
  const [lastSaved, setLastSaved] = useState(null);
  const [autoSaveEnabled, setAutoSaveEnabled] = useState(true); // Toggle for auto-save
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const dragStartRef = useRef({ x: 0, y: 0 });
  const nodeStartPosRef = useRef({ x: 0, y: 0 });
  const saveTimeoutRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  // Load mindmap from API on mount
  useEffect(() => {
    const loadMindMap = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${API_URL}/api/mindmap?map_id=${mapId}`, {
          credentials: 'include',
        });
        
        if (response.ok) {
          const mapData = await response.json();
          
          // If we have saved data, use it
          if (mapData.nodes && Object.keys(mapData.nodes).length > 0) {
            setData({ nodes: mapData.nodes, rootId: null });
            setCustomConnectors(mapData.customConnectors || []);
            setDrawings(mapData.drawings || []);
            setLastSaved(mapData.updated_at ? new Date(mapData.updated_at) : null);
          } else {
            // No saved data, use default
            const defaultData = createDefaultData();
            setData(defaultData);
          }
        } else if (response.status === 401) {
          // Not authenticated, use default data
          const defaultData = createDefaultData();
          setData(defaultData);
        }
      } catch (error) {
        console.error('Failed to load mindmap:', error);
        // On error, use default data
        const defaultData = createDefaultData();
        setData(defaultData);
      } finally {
        setIsLoading(false);
        // Mark initial load as complete after a short delay
        setTimeout(() => {
          isInitialLoadRef.current = false;
        }, 500);
      }
    };
    
    loadMindMap();
  }, [mapId]);

  // Auto-save function
  const saveToServer = useCallback(async (nodes, connectors, drawingsList) => {
    try {
      setSaveStatus('saving');
      
      const response = await fetch(`${API_URL}/api/mindmap?map_id=${mapId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          nodes,
          customConnectors: connectors,
          drawings: drawingsList,
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        setLastSaved(new Date(result.updated_at));
        setSaveStatus('saved');
        
        // Reset to idle after 2 seconds
        setTimeout(() => {
          setSaveStatus('idle');
        }, 2000);
      } else {
        setSaveStatus('error');
      }
    } catch (error) {
      console.error('Failed to save mindmap:', error);
      setSaveStatus('error');
    }
  }, [mapId]);

  // Debounced save - triggers 500ms after last change
  const triggerSave = useCallback(() => {
    // Don't save during initial load
    if (isInitialLoadRef.current) return;
    
    // Mark that we have unsaved changes
    setHasUnsavedChanges(true);
    
    // Don't auto-save if disabled
    if (!autoSaveEnabled) return;
    
    // Clear existing timeout
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Set new timeout
    saveTimeoutRef.current = setTimeout(() => {
      saveToServer(data.nodes, customConnectors, drawings);
      setHasUnsavedChanges(false);
    }, 500);
  }, [data.nodes, customConnectors, drawings, saveToServer, autoSaveEnabled]);

  // Trigger save when data changes
  useEffect(() => {
    triggerSave();
  }, [data.nodes, customConnectors, drawings, triggerSave]);

  // Toggle auto-save and sync immediately if turning ON
  const toggleAutoSave = useCallback(() => {
    setAutoSaveEnabled(prev => {
      const newValue = !prev;
      if (newValue && hasUnsavedChanges) {
        // Turning ON - sync immediately
        saveToServer(data.nodes, customConnectors, drawings);
        setHasUnsavedChanges(false);
      }
      return newValue;
    });
  }, [data.nodes, customConnectors, drawings, hasUnsavedChanges, saveToServer]);

  // Get all nodes as array
  const nodes = Object.values(data.nodes);

  // Get edges (parent-child connections + custom connectors)
  const edges = [
    ...nodes
      .filter(node => node.parentId)
      .map(node => ({
        id: `${node.parentId}-${node.id}`,
        sourceId: node.parentId,
        targetId: node.id,
        type: 'parent-child',
      })),
    ...customConnectors.map(conn => ({
      ...conn,
      type: 'custom',
    })),
  ];

  // Get children of a node
  const getChildren = useCallback((nodeId) => {
    return nodes.filter(node => node.parentId === nodeId);
  }, [nodes]);

  // Check if node is visible (not collapsed by ancestor)
  const isNodeVisible = useCallback((nodeId) => {
    const node = data.nodes[nodeId];
    if (!node) return false;
    if (!node.parentId) return true;
    
    const parent = data.nodes[node.parentId];
    if (!parent) return true;
    if (parent.collapsed) return false;
    
    return isNodeVisible(node.parentId);
  }, [data.nodes]);

  // Add a new child node
  const addChildNode = useCallback((parentId) => {
    const parent = data.nodes[parentId];
    if (!parent) return;

    const newId = uuidv4();
    const children = getChildren(parentId);
    const colorIndex = (children.length + 1) % NODE_COLORS.length;
    
    // Position new node relative to parent
    const angle = (children.length * 60 + 30) * (Math.PI / 180);
    const distance = 200;
    const newX = parent.x + Math.cos(angle) * distance;
    const newY = parent.y + Math.sin(angle) * distance;

    setData(prev => ({
      ...prev,
      nodes: {
        ...prev.nodes,
        [newId]: {
          id: newId,
          title: 'New Node',
          description: 'Click to edit',
          color: NODE_COLORS[colorIndex],
          x: newX,
          y: newY,
          parentId,
          collapsed: false,
        },
      },
    }));

    setSelectedNodeId(newId);
    return newId;
  }, [data.nodes, getChildren]);

  // Add root node (no parent)
  const addRootNode = useCallback(() => {
    const newId = uuidv4();
    const existingRoots = nodes.filter(n => !n.parentId);
    
    setData(prev => ({
      ...prev,
      nodes: {
        ...prev.nodes,
        [newId]: {
          id: newId,
          title: 'New Idea',
          description: 'Click to edit',
          color: NODE_COLORS[existingRoots.length % NODE_COLORS.length],
          x: 400 + existingRoots.length * 100,
          y: 300,
          parentId: null,
          collapsed: false,
        },
      },
    }));

    setSelectedNodeId(newId);
    return newId;
  }, [nodes]);

  // Update node properties
  const updateNode = useCallback((nodeId, updates) => {
    setData(prev => ({
      ...prev,
      nodes: {
        ...prev.nodes,
        [nodeId]: {
          ...prev.nodes[nodeId],
          ...updates,
        },
      },
    }));
  }, []);

  // Delete node and its descendants
  const deleteNode = useCallback((nodeId) => {
    const node = data.nodes[nodeId];
    if (!node) return;

    // Get all descendant IDs
    const getDescendantIds = (id) => {
      const children = getChildren(id);
      return [id, ...children.flatMap(child => getDescendantIds(child.id))];
    };

    const idsToDelete = getDescendantIds(nodeId);

    setData(prev => {
      const newNodes = { ...prev.nodes };
      idsToDelete.forEach(id => delete newNodes[id]);
      return { ...prev, nodes: newNodes };
    });

    if (selectedNodeId && idsToDelete.includes(selectedNodeId)) {
      setSelectedNodeId(null);
    }
  }, [data.nodes, getChildren, selectedNodeId]);

  // Toggle collapse/expand
  const toggleCollapse = useCallback((nodeId) => {
    updateNode(nodeId, { collapsed: !data.nodes[nodeId]?.collapsed });
  }, [data.nodes, updateNode]);

  // Move node position
  const moveNode = useCallback((nodeId, x, y) => {
    updateNode(nodeId, { x, y });
  }, [updateNode]);

  // Pan canvas
  const handlePanStart = useCallback((e) => {
    if (e.target.closest('.mind-map-node')) return;
    setIsPanning(true);
    dragStartRef.current = { x: e.clientX - transform.x, y: e.clientY - transform.y };
  }, [transform]);

  const handlePanMove = useCallback((e) => {
    if (!isPanning) return;
    setTransform(prev => ({
      ...prev,
      x: e.clientX - dragStartRef.current.x,
      y: e.clientY - dragStartRef.current.y,
    }));
  }, [isPanning]);

  const handlePanEnd = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Zoom canvas
  const handleZoom = useCallback((e) => {
    e.preventDefault();
    
    const isPinchGesture = e.ctrlKey || e.metaKey;
    
    let delta;
    if (isPinchGesture) {
      delta = e.deltaY > 0 ? 0.99 : 1.01;
    } else {
      delta = e.deltaY > 0 ? 0.97 : 1.03;
    }
    
    const newScale = Math.min(Math.max(transform.scale * delta, 0.1), 3);
    
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    const newX = mouseX - (mouseX - transform.x) * (newScale / transform.scale);
    const newY = mouseY - (mouseY - transform.y) * (newScale / transform.scale);

    setTransform({ x: newX, y: newY, scale: newScale });
  }, [transform]);

  // Grid size for snap-to-grid (matches dot grid spacing in MindMapCanvas)
  const GRID_SIZE = 30;

  // Snap value to nearest grid point
  const snapToGridValue = useCallback((value) => {
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
  }, []);

  // Ref to track if snap is enabled (controlled externally)
  const snapToGridRef = useRef(false);

  // Function to update snap setting
  const setSnapToGrid = useCallback((enabled) => {
    snapToGridRef.current = enabled;
  }, []);

  // Node drag handlers
  const handleNodeDragStart = useCallback((nodeId, e) => {
    e.stopPropagation();
    setIsDraggingNode(true);
    setSelectedNodeId(nodeId);
    const node = data.nodes[nodeId];
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    nodeStartPosRef.current = { x: node.x, y: node.y };
  }, [data.nodes]);

  const handleNodeDragMove = useCallback((e) => {
    if (!isDraggingNode || !selectedNodeId) return;
    
    const dx = (e.clientX - dragStartRef.current.x) / transform.scale;
    const dy = (e.clientY - dragStartRef.current.y) / transform.scale;
    
    let newX = nodeStartPosRef.current.x + dx;
    let newY = nodeStartPosRef.current.y + dy;
    
    // Apply snap-to-grid if enabled
    if (snapToGridRef.current) {
      newX = snapToGridValue(newX);
      newY = snapToGridValue(newY);
    }
    
    moveNode(selectedNodeId, newX, newY);
  }, [isDraggingNode, selectedNodeId, transform.scale, moveNode, snapToGridValue]);

  const handleNodeDragEnd = useCallback(() => {
    setIsDraggingNode(false);
  }, []);

  // Reset view
  const resetView = useCallback(() => {
    setTransform({ x: 0, y: 0, scale: 1 });
  }, []);

  // Zoom controls
  const zoomIn = useCallback(() => {
    setTransform(prev => ({
      ...prev,
      scale: Math.min(prev.scale * 1.1, 3),
    }));
  }, []);

  const zoomOut = useCallback(() => {
    setTransform(prev => ({
      ...prev,
      scale: Math.max(prev.scale * 0.9, 0.1),
    }));
  }, []);

  // Drawing operations
  const startDrawing = useCallback((x, y) => {
    setCurrentDrawing({
      id: uuidv4(),
      points: [{ x, y }],
      color: drawColor,
      strokeWidth: 2,
    });
  }, [drawColor]);

  const continueDrawing = useCallback((x, y) => {
    if (!currentDrawing) return;
    setCurrentDrawing(prev => ({
      ...prev,
      points: [...prev.points, { x, y }],
    }));
  }, [currentDrawing]);

  const endDrawing = useCallback(() => {
    if (currentDrawing && currentDrawing.points.length > 1) {
      setDrawings(prev => [...prev, currentDrawing]);
    }
    setCurrentDrawing(null);
  }, [currentDrawing]);

  const deleteDrawing = useCallback((drawingId) => {
    setDrawings(prev => prev.filter(d => d.id !== drawingId));
  }, []);

  // Connector operations
  const startConnector = useCallback((nodeId) => {
    setConnectorStart(nodeId);
  }, []);

  const endConnector = useCallback((targetNodeId) => {
    if (connectorStart && connectorStart !== targetNodeId) {
      const newConnector = {
        id: uuidv4(),
        sourceId: connectorStart,
        targetId: targetNodeId,
      };
      setCustomConnectors(prev => [...prev, newConnector]);
    }
    setConnectorStart(null);
  }, [connectorStart]);

  const cancelConnector = useCallback(() => {
    setConnectorStart(null);
  }, []);

  const deleteConnector = useCallback((connectorId) => {
    setCustomConnectors(prev => prev.filter(c => c.id !== connectorId));
  }, []);

  return {
    // Data
    nodes,
    edges,
    data,
    selectedNodeId,
    editingNodeId,
    transform,
    isPanning,
    isDraggingNode,

    // Tool mode
    toolMode,
    setToolMode,

    // Save status
    saveStatus,
    isLoading,
    lastSaved,
    autoSaveEnabled,
    toggleAutoSave,
    hasUnsavedChanges,

    // Drawings
    drawings,
    currentDrawing,
    drawColor,
    setDrawColor,
    startDrawing,
    continueDrawing,
    endDrawing,
    deleteDrawing,

    // Connectors
    customConnectors,
    connectorStart,
    startConnector,
    endConnector,
    cancelConnector,
    deleteConnector,

    // Node operations
    addChildNode,
    addRootNode,
    updateNode,
    deleteNode,
    toggleCollapse,
    moveNode,
    getChildren,
    isNodeVisible,

    // Selection
    setSelectedNodeId,
    setEditingNodeId,

    // Canvas interactions
    handlePanStart,
    handlePanMove,
    handlePanEnd,
    handleZoom,
    handleNodeDragStart,
    handleNodeDragMove,
    handleNodeDragEnd,

    // View controls
    resetView,
    zoomIn,
    zoomOut,
    setTransform,

    // Grid snap
    setSnapToGrid,
  };
};

export default useMindMap;
